import { useAuthStore } from '../stores/auth-store';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  auth?: boolean;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public payload: { error?: string; message?: string; issues?: unknown },
  ) {
    super(payload.message ?? payload.error ?? `Request failed (${status})`);
  }
}

async function tryRefresh(): Promise<string | null> {
  const res = await fetch(`${API_BASE}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!res.ok) {
    useAuthStore.getState().clear();
    return null;
  }
  const data = (await res.json()) as { accessToken: string };
  return data.accessToken;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { auth = true, body, headers, ...rest } = opts;
  const init: RequestInit = {
    ...rest,
    headers: {
      'content-type': 'application/json',
      ...(headers ?? {}),
    },
    credentials: 'include',
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  let accessToken = useAuthStore.getState().accessToken;
  const buildAuthHeader = (token: string | null) => {
    if (auth && token) {
      (init.headers as Record<string, string>)['authorization'] = `Bearer ${token}`;
    }
  };
  buildAuthHeader(accessToken);

  let res = await fetch(`${API_BASE}${path}`, init);
  if (res.status === 401 && auth) {
    accessToken = await tryRefresh();
    if (accessToken) {
      useAuthStore.setState({ accessToken });
      buildAuthHeader(accessToken);
      res = await fetch(`${API_BASE}${path}`, init);
    }
  }

  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
      issues?: unknown;
    };
    throw new ApiError(res.status, payload);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
